/**
 * InputGroup style utilities. Block layout: focus-within, error, disabled states.
 * Inline layout: absolute addon positioning, padding maps.
 * InputGroupButton heights are inset-matched to Input size tokens (sm/md/lg).
 */
import { cn } from "@/utils/cn";
import type { InputSize } from "../../atoms/Input";
import type {
  InputGroupAddonAlign,
  InputGroupEndPadding,
  InputGroupStartPadding,
} from "./InputGroup.types";

/** Base flex wrapper; always fills parent width. */
const WRAPPER_BASE =
  "relative flex w-full min-w-0 items-center transition-colors duration-200";
/** Block: card chrome + focus-within, error, disabled states. */
const WRAPPER_BLOCK = [
  "flex-col rounded-xl border border-dark-invirtus-soft bg-white",
  "focus-within:border-purple focus-within:ring-2 focus-within:ring-purple/20",
  "has-[input[aria-invalid]]:border-red-soft has-[input[aria-invalid]]:focus-within:ring-2 has-[input[aria-invalid]]:focus-within:ring-red-soft/30",
  "has-[input:disabled]:opacity-60 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-background-gray",
].join(" ");

/** In block mode: input loses its own chrome; wrapper owns it. */
const INPUT_BLOCK_OVERRIDES =
  "border-0 bg-transparent rounded-none shadow-none focus:ring-0 focus-visible:ring-0";

/** Inline addon: absolute position left or right, above input chrome. */
const inlineAlignClasses: Record<"inline-start" | "inline-end", string> = {
  "inline-start": "left-0 top-0 bottom-0 z-10 pl-1.5 items-center",
  "inline-end": "right-0 top-0 bottom-0 z-10 pr-1.5 items-center",
};

/** Block addon: padding + flex order. */
const blockAlignClasses: Record<"block-start" | "block-end", string> = {
  "block-start": "px-4 pt-3 order-first",
  "block-end": "px-4 pb-3 order-last",
};

/** Map end padding token → Tailwind (clears right-3 addon + control). */
const END_PADDING_CLASSES: Record<InputGroupEndPadding, string> = {
  none: "",
  icon: "!pr-11",
  button: "!pr-20",
  text: "!pr-24",
};

/** Map start padding token → Tailwind (clears left-3 + icon, matches SearchInput !pl-9). */
const START_PADDING_CLASSES: Record<InputGroupStartPadding, string> = {
  none: "",
  icon: "!pl-9",
  button: "!pl-16",
  text: "!pl-20",
};

/**
 * Contained/outlined button heights inset inside Input of the same size.
 * Input: sm h-7/8, md h-8/9/10, lg h-9/10/11 — button is ~4px shorter each side.
 */
const BUTTON_CONTAINED_SIZE: Record<InputSize, string> = {
  sm: "!h-5 sm:!h-6 !min-h-0 !py-0 !px-2.5 text-xs leading-none",
  md: "!h-6 sm:!h-7 md:!h-8 !min-h-0 !py-0 !px-3 sm:!px-3.5 text-xs sm:text-sm leading-none",
  lg: "!h-7 sm:!h-8 md:!h-9 !min-h-0 !py-0 !px-3.5 sm:!px-4 text-sm leading-none",
};

/** Icon button square matched to the same inset scale. */
const BUTTON_ICON_SIZE: Record<InputSize, string> = {
  sm: "!h-5 sm:!h-6 !w-5 sm:!w-6 !min-h-0 !p-0",
  md: "!h-6 sm:!h-7 md:!h-8 !w-6 sm:!w-7 md:!w-8 !min-h-0 !p-0",
  lg: "!h-7 sm:!h-8 md:!h-9 !w-7 sm:!w-8 md:!w-9 !min-h-0 !p-0",
};

/** Addon: absolute (inline) or flex order (block). */
export function getInputGroupAddonClasses(
  align: InputGroupAddonAlign,
  orientation: "inline" | "block",
  className?: string
): string {
  const isBlock = align.startsWith("block");
  const useBlockLayout = orientation === "block" || isBlock;
  if (useBlockLayout && isBlock) {
    return cn("relative flex", blockAlignClasses[align as "block-start" | "block-end"], className);
  }
  return cn("absolute flex", inlineAlignClasses[align as "inline-start" | "inline-end"], className);
}

/** Input: padding from start/end addon tokens. */
export function getInputGroupInputClasses(
  startPadding: InputGroupStartPadding,
  endPadding: InputGroupEndPadding,
  className?: string
): string {
  return cn(
    "w-full",
    START_PADDING_CLASSES[startPadding],
    END_PADDING_CLASSES[endPadding],
    className
  );
}

/** Wrapper: base + block chrome when orientation=block. */
export function getInputGroupWrapperClasses(
  orientation: "inline" | "block",
  className?: string
): string {
  return cn(WRAPPER_BASE, orientation === "block" && WRAPPER_BLOCK, className);
}

/** Input wrapper: height follows the input; fills row width. */
export function getInputGroupInputWrapperClasses(isBlock: boolean): string {
  return cn("relative flex-1 min-w-0 self-center w-full", isBlock && "w-full");
}

/** Block mode: removes input chrome so wrapper owns focus/error. */
export function getInputGroupInputBlockClasses(isBlock: boolean): string {
  return isBlock ? INPUT_BLOCK_OVERRIDES : "";
}

/**
 * Height/padding overrides so InputGroupButton fits inside Input of the same size.
 * Overrides Button's py-* padding model (incompatible with Input h-* tokens).
 */
export function getInputGroupButtonClasses(
  size: InputSize,
  type: "icon" | "contained" | "outlined" | "text" = "icon"
): string {
  if (type === "icon") return BUTTON_ICON_SIZE[size];
  if (type === "text") return "!min-h-0 !py-0";
  return BUTTON_CONTAINED_SIZE[size];
}
